import { Fragment, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import ModalComponent from "../modal";
import InputComponent from "../input";
import SelectDropdownSecondaryComponent from "../select-dropdown-secondary";
import ButtonComponent from "../button";
import { useFormik } from "formik";
import { participantSchema } from "../../helpers/schemas";
import TextareaComponent from "../textarea";
import { useLocation } from "react-router";
import { templateConstant } from "../../constants/app-constants";
import ImageFieldBase64Component from "../image-field-base64";
import { toast } from "react-toastify";
import IconButtonComponent from "../icon-button";
import { EmptySvg, MinusSvg, PlusSvg, SparksColoredSvg } from "../../assets/svgs";
import StepsProgressBarComponent from "../steps-progress-bar";
import EmptyModalComponent from "../empty-modal";
import { HorizontalLine } from "../../styles/global-styles";
import { ActionsContainer, ActionsContainers, Form, FormError, Label, LabelAction, LabelWrapper } from "./add-update-participant-modal.style";
import CheckboxRadioComponent from "../checkbox-radio";
import MultiSelectDropdownComponent from "../multi-select-dropdown";
import { providerInstitutionConstant } from "../../constants/user-constants";

const AddUpdateParticipantModal = ({ user, data, close, label, handleFormSubmit, update, viewOnly, fromMakerChecker, emptyBinRange, slide}) => {
    const location = useLocation();
    const { schemes } = useSelector((state) => state.schemesListReducer);
    const { logoSizesOptions, logoFormatsOptions, authenticationOptions, otpValidityOptions, otpCharacteristicsOptions, otpDeliveryOptions, smsVendorsOptions, cardValidatorsOptions } = useSelector((state) => state.participantsReducer);
    const isChecker = location.pathname.includes('checker');
    
    // const notProvider = (user?.institution?.id !== providerInstitutionConstant?.id) && (!user?.superAdmin);
    const isProvider = (user?.institution?.id === providerInstitutionConstant?.id) && (user?.superAdmin);
    const isDisabledAll = isChecker || viewOnly;

    const [isDuplicateBin, setIsDuplicateBin] = useState(null);
    const [step, setStep] = useState(1);
    const [hasChanges, setHasChanges] = useState(false);
    const [conditionalRegex, setConditionalRegex] = useState({
        hasSmsDelivery: false,
        hasEmailDelivery: false,
    });

    const { handleChange, handleBlur, handleSubmit, setFieldValue, setFieldTouched, validateForm, touched, errors, values, dirty } = useFormik({
        initialValues: data,
        validationSchema: participantSchema({ isChecker, fromMakerChecker, conditionalRegex }),

       
        onSubmit: (e) => {
            if (!viewOnly && !fromMakerChecker) {
                if (hasChanges) {
                    handleFormSubmit(e);    
                } else {
                    toast.info('No changes found');
                }
            }
        },
    });

    const handleAcceptReject = async (isAccept) => {
        const formErrors = await validateForm();

        if (Object.keys(formErrors).length < 1) {
            const { reason, ...addUpdateParticipantRequest } = values;
            handleFormSubmit({
                isaccepted: isAccept,
                addUpdateParticipantRequest: addUpdateParticipantRequest,
                reason,
            });
        } else {
            handleSubmit();
        }
    };

    const handleImageChange = (name, value) => {
        if (name && value) {
            const img = new Image();
            img.src = value;
            img.onload = () => {
                const width = img.width;
                const height = img.height;
                
                // Extract format from base64 string
                const format = value.split(';')[0].split('/')[1];
                
                if (`${width}px x ${height}px` === values?.logoSize && format === values?.logoFormat) {
                    setFieldValue(name, value);
                } else if (`${width}px x ${height}px` !== values?.logoSize) {
                    toast.error(`Please upload ${values?.logoSize} size image`);
                } else {
                    toast.error(`Please upload image in ${values?.logoFormat} format`);
                }
            };

            img.onerror = () => {
                console.error('Error loading the image');
                toast.error('Error loading the image');
            };
        }
    };

    const toggleObject = (type, name, array, object) => {
        if (!viewOnly) {
            if (type === 'add') {
                setFieldValue(name, [...array, object]);
            } else if (type === 'remove') {
                if (array.length > 1) {
                    setFieldValue(name, array.slice(0, -1));
                }
            }
        }
    };

    const extractNamesByStep = (data) => {
        const result = {};
        data.forEach(item => {
            const step = item.step;
            const names = [];

            const traverse = (node) => {
                if (typeof node === 'object' && node !== null) {
                    for (const key in node) {
                        if (key === 'name') {
                            names.push(node[key]);
                        } else {
                            traverse(node[key]);
                        }
                    }
                }
            };

            traverse(item);
            result[step] = names;
        });
        return result;
    };

    const validateStep = async (currentStep) => {
        if (viewOnly || isChecker) {
            setStep(currentStep + 1);
            return;
        }

        const names = await extractNamesByStep(fields)[currentStep];

        await Promise.all(names.map((name) => setFieldTouched(name, true, true)));

        const formErrors = await validateForm();

        let stepErrors;

        if (errors.addParticipantBinRangeDto && currentStep === 2) {
            stepErrors = errors?.addParticipantBinRangeDto;
        } else {
            stepErrors = names.filter((name) => formErrors?.[name]);
        }

        if (stepErrors.length === 0) {
            if (isDuplicateBin && currentStep === 2) {
                toast.error(isDuplicateBin);
                return;
            } else {
                setStep(currentStep + 1);
            }
        } else {
            toast.error('Please fill in all required fields before proceeding.');
        }
    };

    const handleNumberInput = (e) => {
        const value = e.target.value.replace(/[^0-9]/g, '');
        e.target.value = value;
    };

    const fields = [
        {
            step: 1,
            label: 'Information',
            sections: [
                {
                    label: 'General',
                    fields: [
                        {
                            label: 'Participant Full Name',
                            type: 'text',
                            placeholder: 'Enter participant name',
                            name: 'participantName',
                            value: values?.participantName,
                            disabled: isDisabledAll || update,
                        },
                        // {
                        //     label: 'Participant Type',
                        //     type: (viewOnly || update) ? 'text' : 'select',
                        //     options: participantOptions,
                        //     name: 'participantType',
                        //     value: values?.participantType,
                        //     disabled: isDisabledAll || update,
                        // },
                    ]
                },
                {
                    label: 'Contact Person Details',
                    fields: [
                        {
                            label: 'Primary Name',
                            type: 'text',
                            placeholder: 'Enter primary name',
                            name: 'primaryPersonName',
                            value: values?.primaryPersonName,
                            disabled: isDisabledAll,
                            width: 'col2',
                        },
                        {
                            label: 'Primary Email',
                            type: 'email',
                            placeholder: 'Enter primary email',
                            name: 'primaryPersonEmail',
                            value: values?.primaryPersonEmail,
                            disabled: isDisabledAll,
                            width: 'col2',
                        },
                        {
                            label: 'Primary Mobile Number',
                            type: 'text',
                            placeholder: 'Enter primary mobile number',
                            name: 'primaryPersonPhone',
                            value: values?.primaryPersonPhone,
                            disabled: isDisabledAll,
                            width: 'col2',
                        },
                        {
                            label: 'Secondary Name',
                            type: 'text',
                            placeholder: 'Enter secondary name',
                            name: 'secondaryPersonName',
                            value: values?.secondaryPersonName,
                            disabled: isDisabledAll,
                            width: 'col2',
                        },
                        {
                            label: 'Secondary Email',
                            type: 'email',
                            placeholder: 'Enter secondary email',
                            name: 'secondaryPersonEmail',
                            value: values?.secondaryPersonEmail,
                            disabled: isDisabledAll,
                            width: 'col2',
                        },
                        {
                            label: 'Secondary Mobile Number',
                            type: 'text',
                            placeholder: 'Enter secondary mobile number',
                            name: 'secondaryPersonPhone',
                            value: values?.secondaryPersonPhone,
                            disabled: isDisabledAll,
                            width: 'col2',
                        }
                    ]
                },
                {
                    label: 'Branding',
                    fields: [
                        {
                            label: 'Logo size',
                            type: 'select',
                            options: logoSizesOptions,
                            name: 'logoSize',
                            value: values?.logoSize,
                            handleCustomChange: () => setFieldValue('participantLogo', ''),
                            disabled: isDisabledAll,
                        },
                        {
                            label: 'Logo Format',
                            type: 'select',
                            options: logoFormatsOptions,
                            name: 'logoFormat',
                            value: values?.logoFormat,
                            handleCustomChange: () => setFieldValue('participantLogo', ''),
                            disabled: isDisabledAll,
                        },
                        {
                            label: 'Logo',
                            type: 'file',
                            name: 'participantLogo',
                            value: values?.participantLogo,
                            accept: `image/${values?.logoFormat}`,
                            disabled: isDisabledAll,
                            width: 'col6',
                            hide: (!values?.logoSize && !fromMakerChecker) || (!values?.logoFormat && !fromMakerChecker),
                        },
                    ]
                },
                {
                    type: 'action',
                    actions: [
                        { label: 'Cancel', type: 'button', outline: true, action: close },
                        { label: 'Next', type: 'button', action: () => validateStep(1) }
                    ],
                }
            ]
        },
        {
            step: 2,
            label: 'Bin',
            sections: [
                {
                    type: 'toggleAction',
                    actions: [
                        { label: 'Remove', icon: MinusSvg, type: 'button', outline: true, action: () => toggleObject('remove', 'addParticipantBinRangeDto', values.addParticipantBinRangeDto, emptyBinRange) },
                        { label: 'Add', icon: PlusSvg, type: 'button', action: () => toggleObject('add', 'addParticipantBinRangeDto', values.addParticipantBinRangeDto, emptyBinRange) }
                    ],
                    hide: viewOnly || fromMakerChecker,
                },
                ...(fromMakerChecker && (values?.addParticipantBinRangeDto === undefined || values?.addParticipantBinRangeDto.length < 1)
                    ? []
                    : values?.addParticipantBinRangeDto?.map((binRange, index) => ({
                        fields: [
                            {
                                label: 'Scheme',
                                type: 'select',
                                options: schemes?.map(({ name, id }) => ({ label: name, value: id })),
                                name: `addParticipantBinRangeDto[${index}].schemeId`,
                                value: binRange?.schemeId,
                                handleCustomChange: (e) => { setFieldValue(`addParticipantBinRangeDto[${index}].schemeName`, e?.value) },
                                disabled: isDisabledAll,
                                width: 'col3',
                                error: touched.addParticipantBinRangeDto?.[index]?.schemeId && errors.addParticipantBinRangeDto?.[index]?.schemeId,
                            },
                            {
                                label: 'BIN',
                                type: 'text',
                                onInput: handleNumberInput,
                                name: `addParticipantBinRangeDto[${index}].bin`,
                                value: binRange?.bin,
                                disabled: isDisabledAll,
                                width: 'col1',
                                error: touched.addParticipantBinRangeDto?.[index]?.bin && errors.addParticipantBinRangeDto?.[index]?.bin,
                            },
                            // {
                            //     label: 'ICA',
                            //     type: 'text',
                            //     onInput: handleNumberInput,
                            //     name: `addParticipantBinRangeDto[${index}].ica`,
                            //     value: binRange?.ica,
                            //     disabled: isDisabledAll,
                            //     width: 'col1',
                            //     error: touched.addParticipantBinRangeDto?.[index]?.ica && errors.addParticipantBinRangeDto?.[index]?.ica,
                            // },
                            {
                                label: 'Card Range Start',
                                type: 'text',
                                onInput: handleNumberInput,
                                name: `addParticipantBinRangeDto[${index}].binStartRange`,
                                value: binRange?.binStartRange,
                                disabled: isDisabledAll,
                                width: 'col1',
                                error: touched.addParticipantBinRangeDto?.[index]?.binStartRange && errors.addParticipantBinRangeDto?.[index]?.binStartRange,
                            },
                            {
                                label: 'Card Range End',
                                type: 'text',
                                onInput: handleNumberInput,
                                name: `addParticipantBinRangeDto[${index}].binEndRange`,
                                value: binRange?.binEndRange,
                                disabled: isDisabledAll,
                                width: 'col1',
                                error: touched.addParticipantBinRangeDto?.[index]?.binEndRange && errors.addParticipantBinRangeDto?.[index]?.binEndRange,
                            },
                            // {
                            //     label: 'Currency',
                            //     type: 'multiselect',
                            //     options: countryList?.map(({ currency, currencyCode }) => ({ label: currency, value: currencyCode })),
                            //     name: `addParticipantBinRangeDto[${index}].currencies`,
                            //     value: binRange?.currencies,
                            //     handleCustomChange: (e) => { setFieldValue(`addParticipantBinRangeDto[${index}].currencies`, e?.value) },
                            //     disabled: isDisabledAll,
                            //     width: 'col3',
                            //     error: touched.addParticipantBinRangeDto?.[index]?.currencies && errors.addParticipantBinRangeDto?.[index]?.currencies,
                            // },
                            {
                                type: 'horizontalLine',
                                hide: values?.addParticipantBinRangeDto.length === index + 1,
                            }
                        ]
                    }))),
                {
                    type: 'binError',
                    error: isDuplicateBin,
                    hide: isDisabledAll,
                },
                {
                    type: 'action',
                    actions: [
                        { label: 'Back', type: 'button', outline: true, action: () => setStep(step - 1) },
                        { label: 'Next', type: 'button', action: () => validateStep(2) }
                    ],
                }
            ],
        },
        {
            step: 3,
            label: 'Message Information',
            sections: [
                {
                    label: 'Card Characteristics',
                    fields: [
                        {
                            label: 'Card Validation Managed By',
                            type: 'select',
                            options: cardValidatorsOptions,
                            name: 'cardValidator',
                            value: values?.cardValidator,
                            disabled: isDisabledAll || !isProvider,
                            width: 'col3',
                        },
                    ],
                },
                {
                    label: 'OTP Characteristics',
                    fields: [
                        {
                            label: 'Authentication Type',
                            type: 'select',
                            options: authenticationOptions,
                            name: 'authenticationTypeId',
                            value: values?.authenticationTypeId,
                            // disabled: isDisabledAll,
                            disabled: true,
                            hide: true,
                            width: 'col3',
                        },
                        {
                            label: 'OTP Service Provider',
                            type: 'select',
                            options: smsVendorsOptions,
                            name: 'smsVenderId',
                            value: values?.smsVenderId,
                            disabled: isDisabledAll || !isProvider,
                            width: 'col3',
                        },
                        {
                            label: 'OTP Validity',
                            type: 'select',
                            options: otpValidityOptions,
                            name: 'otpValidityId',
                            value: values?.otpValidityId,
                            disabled: isDisabledAll,
                            width: 'col3',
                        },
                        {
                            label: 'OTP Characteristics',
                            type: 'select',
                            options: otpCharacteristicsOptions,
                            name: 'otpCharacteristicsId',
                            value: values?.otpCharacteristicsId,
                            disabled: isDisabledAll,
                            width: 'col3',
                        },
                        {
                            label: 'Preferred OTP Delivery Methods (Multiple Allowed)', // OTP Medium (Multiple Allowed)
                            type: 'multiselect',
                            options: otpDeliveryOptions,
                            name: 'otpDeliveries',
                            value: values?.otpDeliveries,
                            disabled: isDisabledAll,
                            width: 'col3',
                        },
                        {
                            label: 'Customer Select OTP Medium For Checkout Page',
                            type: 'checkbox',
                            innerLabel: 'OTP Medium Selectable',
                            name: 'customerCanSelectAuthMethod',
                            value: values?.customerCanSelectAuthMethod,
                            disabled: isDisabledAll,
                            width: 'col3',
                        },
                        {
                            label: 'Multi Language Support',
                            type: 'checkbox',
                            innerLabel: 'Enable Multi Language Support',
                            name: 'isMultiLanguageSupport',
                            value: values?.isMultiLanguageSupport,
                            disabled: isDisabledAll,
                            width: 'col3',
                        },
                    ]
                },
                {
                    label: 'SMS OTP Configuration',
                    fields: [
                        {
                            label: 'SMS Template',
                            type: 'textarea',
                            placeholder: 'Set SMS template',
                            name: 'smsTemplate',
                            value: values?.smsTemplate,
                            disabled: isDisabledAll,
                            width: 'col6',
                            labelAction: {
                                label: 'Auto generate',
                                icon: SparksColoredSvg,
                                action: () => {
                                    setFieldValue('smsTemplate', templateConstant?.sms);
                                }
                            },
                        },
                    ],
                    hide: !conditionalRegex?.hasSmsDelivery,
                },
                {
                    label: 'Email OTP Configuration',
                    fields: [
                        {
                            label: 'Email OTP Sender ID',
                            type: 'text',
                            placeholder: 'Enter email OTP sender ID',
                            name: 'senderEmail',
                            value: values?.senderEmail,
                            disabled: isDisabledAll,
                        },
                        {
                            label: 'Email OTP Subject Line',
                            type: 'text',
                            placeholder: 'Enter email OTP subject line',
                            name: 'emailSubject',
                            value: values?.emailSubject,
                            disabled: isDisabledAll,
                        },
                        {
                            label: 'Email OTP Template',
                            type: 'textarea',
                            placeholder: 'Set email OTP template',
                            name: 'emailTemplate',
                            value: values?.emailTemplate,
                            disabled: isDisabledAll,
                            width: 'col6',
                            labelAction: {
                                label: 'Auto generate',
                                icon: SparksColoredSvg,
                                action: () => {
                                    setFieldValue('emailTemplate', templateConstant?.email);
                                }
                            },
                        },
                    ],
                    hide: !conditionalRegex?.hasEmailDelivery,
                },
                ...(fromMakerChecker ? [{
                    label: 'Reason',
                    fields: [
                        {
                            label: 'Comments',
                            type: 'textarea',
                            placeholder: 'Enter Comments',
                            name: 'reason',
                            value: values?.reason,
                            width: 'col6'
                        }
                    ],
                }] : []),
                {
                    type: 'action',
                    actions: fromMakerChecker ? [
                        { label: 'Back', type: 'button', outline: true, action: () => setStep(step - 1) },
                        { label: 'Reject', type: 'button', color: 'error', outline: true, action: () => handleAcceptReject(false) },
                        { label: isChecker ? 'Approve' : 'Resend', type: 'button', action: () => handleAcceptReject(true) }
                    ] : [
                        { label: 'Back', type: 'button', outline: true, action: () => setStep(step - 1) },
                        { label: viewOnly ? 'Close' : update ? 'Update' : 'Submit', type: viewOnly ? 'button' : 'submit', action: () => { viewOnly && close() } }
                    ]
                }
            ],
        }
    ];

    useEffect(() => {
        // Check BIN or ICA duplications
        if (values?.addParticipantBinRangeDto?.length > 0) {
            const bins = values.addParticipantBinRangeDto.map(item => item.bin.toString());
            // const icas = values.addParticipantBinRangeDto.map(item => item.ica.toString());

            const hasDuplicateBin = bins.some((bin, index) => bins.indexOf(bin) !== index);
            // const hasDuplicateIca = icas.some((ica, index) => icas.indexOf(ica) !== index);

            // if (hasDuplicateBin || hasDuplicateIca) {
            //     const message = hasDuplicateBin
            //         ? 'Duplicate BIN found. Each BIN must be unique.'
            //         : 'Duplicate ICA found. Each ICA must be unique.';
            //     setIsDuplicateBin(message);
            // } else {
            //     setIsDuplicateBin(null);
            // }
            if (hasDuplicateBin) {
                const message = hasDuplicateBin
                    ? `Duplicate BIN found {${bins.find(bin => bins.indexOf(bin) !== bins.lastIndexOf(bin))}}. Each BIN must be unique.`
                    : 'Duplicate ICA found. Each ICA must be unique.';
                setIsDuplicateBin(message);
            } else {
                setIsDuplicateBin(null);
            }
        }

        // Check SMS and Email delivery
        const smsOption = otpDeliveryOptions?.find(e => e.label.toLowerCase().includes('sms'));
        const emailOption = otpDeliveryOptions?.find(e => e.label.toLowerCase().includes('email'));

        const hasSmsDelivery = values?.otpDeliveries?.includes(smsOption?.value) || false;
        const hasEmailDelivery = values?.otpDeliveries?.includes(emailOption?.value) || false;

        setConditionalRegex((old) => ({
            ...old,
            hasSmsDelivery,
            hasEmailDelivery
        }));
    }, [values]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setHasChanges(dirty);
    }, [dirty]);

    return (
        <ModalComponent close={close} label={label} width={1100} slide={slide}>
            {/* MODAL BODY */}
            <Form onSubmit={handleSubmit}>
                <div className="col6">
                    <StepsProgressBarComponent currentStep={step}
                        data={
                            fields
                                .filter((e) => !e.hide)
                                .map((e) => ({ step: e.step, label: e.label }))
                        }
                    />
                </div>

                {
                    step === 2 && (!values?.addParticipantBinRangeDto || values?.addParticipantBinRangeDto?.length < 1) &&
                    <div className="col6">
                        <EmptyModalComponent icon={EmptySvg} text="No bin found!" />
                    </div>
                }

                {
                    fields && fields.length > 0 && fields.map((item, mainIndex) => (
                        <Fragment key={mainIndex}>
                            {
                                !item.hide && item.step === step && item.sections && item.sections.length > 0 && item.sections.map((section, sectionIndex) => (
                                    <Fragment key={sectionIndex}>
                                        {
                                            section.type === 'toggleAction' && section.actions.length > 0 && !section.hide &&
                                            <div className='col6'>
                                                <ActionsContainer>
                                                    {
                                                        section.actions.map((button, index) => (
                                                            <IconButtonComponent key={index} icon={button.icon} type={button.type} outline={button.outline} onClick={button.action} />
                                                        ))
                                                    }
                                                </ActionsContainer>
                                            </div>
                                        }
                                        {
                                            section.label && !section.hide &&
                                            <h5 className="col6">{section.label}</h5>
                                        }
                                        {
                                            !section.hide && section.fields && section.fields.length > 0 && section.fields.map((field, index) => (
                                                <Fragment key={index}>
                                                    {
                                                        ['text', 'number', 'email', 'password'].includes(field.type) && !field.hide &&
                                                        <div className={field.width ? field.width : 'col3'}>
                                                            {/* <Label>{field.label}</Label> */}
                                                            <InputComponent
                                                                type={field.type}
                                                                onInput={field.onInput}
                                                                label={field.label}
                                                                placeholder={field.placeholder}
                                                                onBlur={handleBlur}
                                                                name={field.name}
                                                                value={field.value}
                                                                onChange={handleChange}
                                                                disabled={field.disabled}
                                                                mui
                                                            />
                                                            {
                                                                (field?.error || (touched[field.name] && errors[field.name])) && (
                                                                    <FormError>{field.error || errors[field.name]}</FormError>
                                                                )
                                                            }
                                                        </div>
                                                    }

                                                    {
                                                        field.type === 'file' && !field.hide &&
                                                        <div className={field.width ? field.width : 'col3'}>
                                                            <Label>{field.label}</Label>
                                                            <ImageFieldBase64Component
                                                                name={field.name}
                                                                value={field.value}
                                                                accept={field.accept}
                                                                onChange={(fileData) => handleImageChange(field.name, fileData.value)}
                                                                disabled={field.disabled}
                                                                valueMust
                                                            />
                                                            {
                                                                (field?.error || (touched[field.name] && errors[field.name])) && (
                                                                    <FormError>{field.error || errors[field.name]}</FormError>
                                                                )
                                                            }
                                                        </div>
                                                    }

                                                    {
                                                        field.type === 'select' && !field.hide &&
                                                        <div className={field.width ? field.width : 'col3'}>
                                                            {/* <Label>{field.label}</Label> */}
                                                            <SelectDropdownSecondaryComponent
                                                                label={field.label}
                                                                options={field.options}
                                                                name={field.name}
                                                                value={field.value}
                                                                onChange={(e) => { handleChange(e); field.handleCustomChange && field.handleCustomChange(e.target) }}
                                                                disabled={field.disabled}
                                                                mui
                                                            />
                                                            {
                                                                (field?.error || (touched[field.name] && errors[field.name])) && (
                                                                    <FormError>{field.error || errors[field.name]}</FormError>
                                                                )
                                                            }
                                                        </div>
                                                    }

                                                    {
                                                        field.type === 'multiselect' && !field.hide &&
                                                        <div className={field.width ? field.width : 'col3'}>
                                                            {/* <Label>{field.label}</Label> */}
                                                            <MultiSelectDropdownComponent
                                                                label={field.label}
                                                                options={field.options}
                                                                name={field.name}
                                                                value={field.value}
                                                                onChange={(e) => { handleChange(e); field.handleCustomChange && field.handleCustomChange(e.target) }}
                                                                disabled={field.disabled}
                                                                mui
                                                            />
                                                            {
                                                                (field?.error || (touched[field.name] && errors[field.name])) && (
                                                                    <FormError>{field.error || errors[field.name]}</FormError>
                                                                )
                                                            }
                                                        </div>
                                                    }

                                                    {
                                                        ['checkbox', 'radio'].includes(field.type) && !field.hide &&
                                                        <div className={field.width ? field.width : 'col3'}>
                                                            <Label>{field.label}</Label>
                                                            <CheckboxRadioComponent
                                                                type={field.type}
                                                                label={field.innerLabel}
                                                                name={field.name}
                                                                checked={field.value}
                                                                onChange={handleChange}
                                                                disabled={field.disabled}
                                                            />
                                                            {
                                                                (field?.error || (touched[field.name] && errors[field.name])) && (
                                                                    <FormError>{field.error || errors[field.name]}</FormError>
                                                                )
                                                            }
                                                        </div>
                                                    }

                                                    {
                                                        field.type === 'textarea' && !field.hide &&
                                                        <div className={field.width ? field.width : 'col3'}>
                                                            <LabelWrapper>
                                                                {/* <Label>{field.label}</Label> */}
                                                                {
                                                                    field?.labelAction && !field.disabled && (
                                                                        <LabelAction onClick={field?.labelAction?.action}>
                                                                            <field.labelAction.icon />
                                                                            {field?.labelAction?.label}
                                                                        </LabelAction>
                                                                    )
                                                                }
                                                            </LabelWrapper>
                                                            <TextareaComponent
                                                                label={field.label}
                                                                placeholder={field.placeholder}
                                                                onBlur={handleBlur}
                                                                name={field.name}
                                                                value={field.value}
                                                                required={field.required}
                                                                onChange={handleChange}
                                                                disabled={field.disabled}
                                                                mui
                                                            />
                                                            {
                                                                (field?.error || (touched[field.name] && errors[field.name])) && (
                                                                    <FormError>{field.error || errors[field.name]}</FormError>
                                                                )
                                                            }
                                                        </div>
                                                    }

                                                    {
                                                        field.type === 'horizontalLine' && !field.hide &&
                                                        <HorizontalLine className="col6" />
                                                    }
                                                </Fragment>
                                            ))
                                        }
                                        {
                                            section.type === 'binError' && section.error && !section.hide &&
                                            <div className='col6'>
                                                <FormError>{section.error}</FormError>
                                            </div>
                                        }
                                        {
                                            section.type === 'action' && section.actions.length > 0 && !section.hide &&
                                            <div className='col6'>
                                                <ActionsContainers>
                                                    {
                                                        section.actions.map((button, index) => (
                                                            <ButtonComponent key={index} label={button.label} type={button.type} outline={button.outline} color={button?.color} onClick={button.action} />
                                                        ))
                                                    }
                                                </ActionsContainers>
                                            </div>
                                        }
                                    </Fragment>
                                ))
                            }
                        </Fragment>
                    ))
                }
            </Form>
        </ModalComponent>
    );
};

export default AddUpdateParticipantModal;